import type { ExperimentChecker } from "./ExperimentChecker";
import { checkExperimentBase } from "./ExperimentChecker";
import {
  staticRibbonExperiments,
  isShyRibbonFeatureEnabled as isShyRibbonFeatureEnabledGeneric,
} from "./staticRibbonExperiments";
import { fluentExperiments } from "./fluentExperiments";

// List of all shield change/feature gates that must be checked at the acui level. If your fix can be checked at the ooux level or app level, use ShieldSettings.ts
const shieldExperiments = {
  colorPickerDialogTooltipFixIsEnabled:
    "SharedOnline.ChangeGate.ColorPickerDialogTooltipFixIsEnabled",
  dialogCharacterFixEnabled:
    "SharedOnline.ChangeGate.DialogSpecialCharacterFixIsEnabled",
  AppGalleryHighContrastBackgroundFix:
    "SharedOnline.ChangeGate.AppGalleryHighContrastBackgroundFix",
  extendedOcclusionCheckLogicIsEnabled:
    "SharedOnline.ChangeGate.ExtendedOcclusionCheckLogicIsEnabled",
  AppSpinButtonPaddingFix: "SharedOnline.ChangeGate.AppSpinButtonPaddingFix",
  draggableFloatieMoveFixIsEnabled:
    "SharedOnline.ChangeGate.DraggableFloatieMoveFixIsEnabled",
  AppSpinButtonUnitPlaceholderFix:
    "SharedOnline.ChangeGate.AppSpinButtonUnitPlaceholderFix",
  findDOMNodeRemoved: "SharedOnline.ChangeGate.FindDOMNodeRemoved",
  contextMenuUnmountFixIsEnabled:
    "SharedOnline.ChangeGate.ContextMenuUnmountFixIsEnabled",
};

const consumerExperiments = {
  displayFreeFontsFirstFlightEnabled:
    "Microsoft.Office.WordOnline.DisplayFreeFontsFirst",
  isProTipSuggestionEnabled:
    "Microsoft.Office.WordOnline.ProTipSuggestionEnabled",
};

export const ribbonExperiments = {
  renderInitiallyEnabledRibbonIsEnabled:
    "RenderInitiallyEnabledRibbonIsEnabled",
  updatedTellMePlaceholderIsEnabled: "UpdatedTellMePlaceholderIsEnabled",
  multilineRibbonIsEnabled: "ReactMultilineRibbonIsEnabled",
  taskPaneResizingIsEnabled: "TaskPaneResizingIsEnabled",
  activateComboAndSpinControlOnEnter:
    "ActivateComboAndSpinMenuItemOnEnterIsEnabled",
  chatPPTEnabled: "Microsoft.Office.PowerPointOnline.ChatPPTOnline",
  ribbonCachedScalingIsEnabled: "ReactRibbonCachedScalingIsEnabled",
  delayRenderTooltipsIsEnabled:
    "SharedOnline.ChangeGate.DelayRenderTooltipsIsEnabled",
  disableRibbonAnimationComponents: "DisableAnimationComponentsIsEnabled",
  delayInstallAddInIsEnabled: "DelayInstallAddInIsEnabled",
  tooltipsTargetOverflowSetItemEnabled: "TooltipsTargetOverflowSetItemEnabled",
  disableColorPickerFancyTooltips: "DisableColorPickerFancyTooltips",
  batchTopBarScaleDataInRibbon: "BatchTopBarScaleDataInRibbon",
  suppressCalloutWithOccludedTargetIsEnabled:
    "SuppressCalloutWithOccludedTargetIsEnabled",
  decentralizedScalingLogicIsEnabled:
    "Microsoft.Office.SharedOnline.DecentralizedScalingLogicIsEnabled",
  perfMarkerThrottlingOverrideIsEnabled:
    "Microsoft.Office.SharedOnline.PerfMarkerThrottlingOverrideIsEnabled",
  shouldContextualTabUseNormalTabStyles:
    "ShouldContextualTabUseNormalTabStyles",
  zoomSliderIsEnabled: "ZoomSliderIsEnabled",
  shouldMergeAppToggleButtonMenuItemStyles:
    "ShouldMergeAppToggleButtonMenuItemStyles",
  multiWindowSupportIsEnabled:
    "SharedOnline.ChangeGate.MultiWindowSupportIsEnabled",
  multiTaskPaneOpenFixIsEnabled:
    "SharedOnline.ChangeGate.MultiTaskPaneOpenFixIsEnabled",
  customAnonymousNameIsEnabled:
    "Microsoft.Office.SharedOnline.IsCustomAnonymousNameEnabled",
  taskPaneTabsterFixEnabled:
    "SharedOnline.ChangeGate.TaskPaneTabsterFixEnabled",
  restoreContextMenuFocusIsEnabled:
    "SharedOnline.ChangeGate.RestoreContextMenuFocusIsEnabled",
  ribbonBottomBarMemoryLeakFix:
    "SharedOnline.ChangeGate.RibbonBottomBarMemoryLeakFix",
  comboBoxShadowRenderingFix:
    "SharedOnline.ChangeGate.ComboBoxShadowRenderingFix",
  preventMenuExecutionV9: "SharedOnline.ChangeGate.PreventMenuExecutionV9",
  useFullDescriptionLabelOnly:
    "SharedOnline.ChangeGate.UseFullDescriptionLabelOnly",
  closeMenuOnUnmount: "SharedOnline.ChangeGate.CloseMenuOnUnmount",
  appTooltipAppContextIsEnabled:
    "SharedOnline.ChangeGate.AppTooltipAppContextIsEnabled",
  headerIcon: "SharedOnline.ChangeGate.HeaderIcon",
  singleLineToolBar: "SharedOnline.IsSingleLineToolbarEnabled",
  modeSwitcherOverlapFix: "SharedOnline.ChangeGate.ModeSwitcherOverlapFix",
  undefinedCheckForHeaderCoordinates:
    "SharedOnline.ChangeGate.UndefinedCheckForHeaderCoordinates",
  controlIdLoggingIsEnabled:
    "SharedOnline.ChangeGate.OfficeVSO_8561837_EnableControlIDDatafields",
  controlsPerLayoutScalingFix:
    "SharedOnline.ChangeGate.ControlsPerLayoutScalingFix",
  addTaskpaneHeaderPreviewTag:
    "SharedOnline.ChangeGate.AddTaskpaneHeaderPreviewTag",
  globalThemePaddingFix: "SharedOnline.ChangeGate.GlobalThemePaddingFix",
  appGalleryPreviewButtonIdIsEnabled: "AppGalleryPreviewButtonIdIsEnabled",
  mfpRemoveUnnecessaryRequestAnimationFrame:
    "Microsoft.Office.SharedOnline.ModernFontPickerRemoveUnnecessaryRequestAnimationFrameIsEnabled",
  mfpDeferOnMenuDismissedEnabled:
    "Microsoft.Office.SharedOnline.ModernFontPickerDeferOnMenuDismissedIsEnabled",
  hideNumberOfPresentUsers:
    "SharedOnline.ChangeGate.HideNumberOfPresentUsersInCoauthMenu",
  copilotMaxTaskpaneWidthIsEnabled:
    "Microsoft.Office.WordOnline.CopilotTaskPaneMaxWidth",
  messageBarUpdatesEnabled: "SharedOnline.ChangeGate.MessageBarUpdatesEnabled",
  paddlesFixIsEnabled: "SharedOnline.ChangeGate.PaddlesFixIsEnabled",
  appGallerySplitButtonStylesFixEnabled:
    "SharedOnline.ChangeGate.AppGallerySplitButtonTextAlignmentFixEnabled",
  updatedPaddleOffsetEnabled:
    "SharedOnline.ChangeGate.UpdatedPaddleOffsetEnabled",
  menuButtonIconForLargeFlyoutAnchorsEnabled:
    "SharedOnline.ChangeGate.MenuButtonIconForLargeFlyoutAnchorsEnabled",
  isNewFluentIconsEnabled: "Microsoft.Office.SharedOnline.FluentIconsEnabled",
  isV9FileMenuDismissOnButtonClickFixEnabled:
    "SharedOnline.ChangeGate.OfficeVSO_8691483_FileMenuDismissOnButtonClickEnabled",
  v9MenuAutoDismissFixEnabled:
    "SharedOnline.ChangeGate.v9MenuAutoDismissFixEnabled",
  isKeytipsForV9MenuItemsEnabled:
    "SharedOnline.ChangeGate.OfficeVSO:8923223_v9MenuItems_KeytipsEnabled",
  isFloatieStylesGalleryButtonFixEnabled:
    "Microsoft.Office.WordOnline.FloatieStylesGalleryButtonFixEnabled",
  isMenuButtonRefFixEnabled:
    "SharedOnline.ChangeGate.OfficeVSO:8941200_v9menuButtonRefFixEnabled",
  fixIncorrectMenuShownDuringSearch:
    "SharedOnline.ChangeGate.FixIncorrectMenuShownDuringSearch",
  fixOnMenuOpenedTriggerOnSubmenuHover:
    "SharedOnline.ChangeGate.FixOnMenuOpenedTriggerOnSubmenuHover",
  isFluentV9DispatchOptimizationEnabled:
    "SharedOnline.FluentV9DispatchOptimizationEnabled",
  isFluentV9FloatieDispatchOptimizationEnabled:
    "SharedOnline.FluentV9FloatieDispatchOptimizationEnabled",
  hideCustomTooltipInV9AppControlsEnabled:
    "SharedOnline.ChangeGate.HideCustomTooltipInV9AppControlsEnabled",
  triggerOnMenuDismissedOnSubmenuClose:
    "SharedOnline.ChangeGate.TriggerOnMenuDismissedOnSubmenuClose",
  fluentV9ColorPickerIsEnabled: "SharedOnline.FluentV9ColorPickerEnabled",
  fluentV9PreviewGalleryIsEnabled: "SharedOnline.FluentV9PreviewGalleryEnabled",
  stackScopedSelectorPropIsEnabled:
    "SharedOnline.ChangeGate.StackScopedSelectorPropIsEnabled",
  ...shieldExperiments,
  ...consumerExperiments,
  ...staticRibbonExperiments,
  ...fluentExperiments,
};

export const platformFeatures = {
  disableDelayRenderToolTipMultiWindow:
    "DisableDelayRenderTooltipForMultiWindow",
  modalContextMenuIsEnabled: "ModalContextMenuIsEnabled",
  tooltipForMacIsEnabled: "TooltipForMacIsEnabled",
};

export const checkExperiment = (
  context: ExperimentChecker,
  feature: string
) => {
  return checkExperimentBase(context, feature);
};

export const isShyRibbonEnabled = (context: ExperimentChecker) => {
  return isShyRibbonFeatureEnabled(context.isExperimentalFeatureEnabled);
};

export const isChatPPTEnabled = (context: ExperimentChecker) => {
  return isChatPPTFeatureEnabled(context.isExperimentalFeatureEnabled);
};

export const isShyRibbonFeatureEnabled = (
  isFeatureEnabled?: (
    feature: string,
    defaultValue?: boolean | undefined
  ) => boolean
) => {
  return isShyRibbonFeatureEnabledGeneric(isFeatureEnabled);
};

export const isChatPPTFeatureEnabled = (
  isFeatureEnabled?: (
    feature: string,
    defaultValue?: boolean | undefined
  ) => boolean
) => {
  return (
    isFeatureEnabled !== undefined &&
    isFeatureEnabled(ribbonExperiments.chatPPTEnabled)
  );
};
